/**
 * Utility functions for markdown text processing
 */

/**
 * Strips markdown syntax and converts to plain text suitable for subtitles
 * Removes:
 * - Heading markers (###, ####, etc.)
 * - Multiple newlines (keeps single spaces)
 * - Code blocks and inline code markers
 * - Link syntax (keeps link text only)
 * - Image syntax
 * - Blockquotes
 * - Horizontal rules
 * - List markers
 * 
 * Preserves:
 * - Basic text emphasis indicators (**, *, ~~) for visual rendering
 * - Single line breaks as spaces
 * 
 * @param text - The markdown text to clean
 * @returns Clean text suitable for subtitle display
 */
export function stripMarkdownForSubtitles(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    // Remove code blocks (``` or ~~~)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/~~~[\s\S]*?~~~/g, '')
    
    // Replace multiple newlines with single space (do this early)
    .replace(/\n{2,}/g, '\n')
    
    // Remove heading markers (###, ####, etc.) but keep the text
    // Must handle both line start and after newlines
    .replace(/(?:^|\n)\s*#{1,6}\s+/g, ' ')
    
    // Remove horizontal rules
    .replace(/^[-*_]{3,}\s*$/gm, '')
    
    // Remove blockquote markers
    .replace(/^>\s+/gm, '')
    
    // Remove list markers (ordered and unordered)
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    
    // Convert links [text](url) to just text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    
    // Remove images ![alt](url)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
    
    // Remove inline code backticks (keep the text)
    .replace(/`([^`]+)`/g, '$1')
    
    // Remove HTML tags if any
    .replace(/<[^>]+>/g, '')
    
    // Replace remaining newlines with space
    .replace(/\n/g, ' ')
    
    // Replace multiple spaces with single space
    .replace(/\s{2,}/g, ' ')
    
    // Trim whitespace
    .trim();
}

/**
 * Converts basic markdown formatting to HTML for subtitle display
 * Only processes inline elements suitable for subtitles:
 * - Bold (**text** or __text__)
 * - Italic (*text* or _text_)
 * - Strikethrough (~~text~~)
 * 
 * All block-level markdown is stripped first
 * 
 * @param text - The markdown text to convert
 * @returns HTML string with inline formatting only
 */
export function markdownToSubtitleHtml(text: string): string {
  // First strip all block-level markdown
  const cleanText = stripMarkdownForSubtitles(text);
  
  if (!cleanText) {
    return '';
  }

  return cleanText
    // Bold text **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    
    // Italic text *text* or _text_ (avoid matching bold markers)
    .replace(/(?<!\*)\*([^*\s][^*]*?[^*\s]?)\*(?!\*)/g, '<em>$1</em>')
    .replace(/(?<!_)_([^_\s][^_]*?[^_\s]?)_(?!_)/g, '<em>$1</em>')
    
    // Strikethrough ~~text~~
    .replace(/~~(.*?)~~/g, '<del>$1</del>');
}
